// @ts-nocheck
import { route } from 'quasar/wrappers';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';
import routes from './routes';
// Direct imports (not via src/store/main barrel) — the router is loaded in the
// boot entry, and pulling the barrel would drag 60+ unrelated stores into the
// initial chunk. See CLAUDE.md "Build Optimization" section.
import { useAuthTokenStore } from 'src/store/User/auth_token.store';
import { useSettingStore } from 'src/store/Settings/setting.store';
import { Acl } from 'src/core/acls/index';
import RouteHooks from './hooks/main';
import { LocalStorage, SessionStorage } from 'quasar';
import { notify } from 'src/components/notifications/notify';
import logger from 'src/utils/logger';
import { isSupplierUser } from 'src/utils/UserDetails';
import { isSupplierAllowedSettingsPath, isRouteBlockedForSupplier } from './supplier-settings-access';
import { withGuardTimeout } from './withGuardTimeout';

// DPT-252 / fix-noauth-login-timeout: every awaited backend call in the
// beforeEach guard is bounded so a stalled request can never block next() and
// strand the SPA on an endless spinner. Budgets are generous enough that a
// merely slow/cold backend (2-5s in practice) still completes normally; only a
// true hang trips the fallback. See withGuardTimeout for the full rationale.
const GUARD_TIMEOUT_MS = {
  // isLoggedIn() may refresh the token, and fetchFreshToken retries up to 3x
  // with exponential backoff — worst case ~12s on a flaky-but-working backend.
  // 15s keeps the timeout above the full retry chain so a legitimate slow
  // refresh is never aborted into a false logout; it only fires on a true hang.
  auth: 15000,
  catalog: 6000, // dashboard search-field catalog hydration
  acl: 6000, // ACL hydration
};
/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: (to) => {
      // Only use hash as scroll target if it looks like a valid element id selector
      // (e.g. #section-1). Skip route-like hashes (e.g. #/login) to avoid
      // Vue Router querySelector warning.
      if (to.hash && /^#[a-zA-Z][\w-]*$/.test(to.hash)) {
        return {
          el: to.hash,
          behavior: 'smooth',
        };
      }
      return { left: 0, top: 0 };
    },
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(
      process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE,
    ),
  });

  // ✅ CRITICAL: Track if we've marked initialization as complete
  // This ensures we only call markInitializationComplete() once after first authenticated route
  let initializationMarked = false;

  // ✅ FIX: Detect infinite redirect loops from auth failures
  // Tracks how many times we redirect TO /login in rapid succession
  let redirectToLoginCount = 0;
  let lastRedirectToLoginTime = 0;

  Router.beforeEach(async (to, from, next) => {
    console.log(`[ROUTER GUARD] Navigation: ${from.path} → ${to.path}`);
    new RouteHooks(to, from);

    // ✅ FIX: Detect and break infinite redirect loops
    // When token refresh fails, corrupted token can cause repeated redirects
    if (to.path === '/login' && from.path !== '/login' && from.path !== '/') {
      const now = Date.now();
      if (now - lastRedirectToLoginTime < 5000) {
        redirectToLoginCount++;
      } else {
        redirectToLoginCount = 1;
      }
      lastRedirectToLoginTime = now;

      if (redirectToLoginCount > 3) {
        console.error(
          `[ROUTER GUARD] INFINITE LOOP DETECTED: ${redirectToLoginCount} redirects to /login in 5 seconds`,
        );
        const auth_token_store = useAuthTokenStore();
        LocalStorage.clear();
        SessionStorage.clear();
        auth_token_store.$reset();
        redirectToLoginCount = 0;

        notify.error('Authentication failed. Please refresh the page and try again.');
      }
    }

    // Language resolution lives in `src/boot/tolgee.js`, which runs once at
    // startup: it applies the stored/browser/default language before first
    // paint and, if a token is already present, restores the backend
    // PREFERRED_LANGUAGE. The router no longer re-resolves language on every
    // navigation.

    if (!to.meta.requiresAuth) {
      // Public routes (like login) - skip auth checks
      logger.router('Public route detected:', to.path, '- skipping auth checks');
      next();
      return false;
    }

    const auth_token_store = useAuthTokenStore();

    // ✅ FIX: Hydrate Pinia state from LocalStorage before any auth checks
    // This fixes race conditions where token exists in LocalStorage but Pinia state is empty
    // (e.g., after page refresh or direct navigation to protected routes like /settings/*)
    auth_token_store.hydrateFromLocalStorage();

    // Check token status early - required for all protected routes.
    // Bounded: a valid (non-expired) token returns synchronously and never hits
    // the timeout; only an expired token whose refresh request STALLS does. In
    // that case the refresh has effectively failed, so treating the user as not
    // authenticated (-> redirect to /login) is both correct and recoverable —
    // far better than hanging the boot forever (DPT-252).
    const canAccess = await withGuardTimeout(
      auth_token_store.isLoggedIn(),
      GUARD_TIMEOUT_MS.auth,
      false,
      () =>
        logger.warn(
          `[ROUTER] auth check stalled >${GUARD_TIMEOUT_MS.auth}ms — treating as not authenticated so navigation can proceed`,
        ),
    );

    // SPECIAL CASE: Allow /prepare-dashboard to show its own internal state
    // instead of redirecting away — but only if the user is already logged in
    // (valid token). prepare-dashboard owns cross-region reconciliation now: it
    // fetches the profile, and on a region mismatch persists the target region
    // and hard-reloads onto the correct cluster (DOCB-12864).
    if (to.path === '/prepare-dashboard') {
      if (!canAccess) {
        // Redirect to login if not authenticated
        next({
          path: '/login',
          query: { redirect: to.fullPath },
        });
        return false;
      }

      next();
      return;
    }
    if (to.meta.requiresAuth && !canAccess) {
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      });
      return false;
    }

    // Supplier gate — runs BEFORE the admin check and regardless of the org-admin
    // flag. A supplier can be an admin of their own sub-org, so isAdmin() may be
    // true for them; that must not unlock the parent org's settings pages.
    // Policy + allowlist live in ./supplier-settings-access (kept in sync with
    // the supplier filtering in src/store/settings-navigation.ts).
    if (
      to.path.startsWith('/settings') &&
      isSupplierUser() &&
      !isSupplierAllowedSettingsPath(to.path)
    ) {
      next({ path: '/settings' });
      return false;
    }

    // Routes flagged blockedForSupplier (e.g. open search, knowledge graph) are
    // hidden from the supplier nav; also block direct-URL access for suppliers.
    if (isRouteBlockedForSupplier(to.meta) && isSupplierUser()) {
      next({ path: '/dashboard' });
      return false;
    }

    // Admin check AFTER auth check — authenticated but non-admin users
    // should NOT be sent to /login (they are already authenticated).
    // Settings sub-routes redirect to /settings which shows "Admin Access Required" UI;
    // other admin-only routes redirect to /dashboard.
    if (to.meta.requiresAdmin && !auth_token_store.isAdmin()) {
      // Allow supplier/non-admin users to reach supplier-related settings routes.
      // The category landing pages a supplier sees are additionally allowed via
      // the shared supplier policy so a non-admin supplier isn't bounced off
      // /settings/category/organization etc.
      const supplierAllowedPaths = ['/settings/supplier/general', '/settings/users'];
      const isSupplierAllowed =
        (to.path.startsWith('/settings') && supplierAllowedPaths.some(p => to.path.startsWith(p))) ||
        (isSupplierUser() && isSupplierAllowedSettingsPath(to.path));
      if (!isSupplierAllowed) {
        const redirectPath = to.path.startsWith('/settings') ? '/settings' : '/dashboard';
        next({ path: redirectPath });
        return false;
      }
    }

    // User is logged in and route is allowed - restore language from backend
    const settings_store = useSettingStore();

    // ✅ FIX: Only fetch settings if we have a valid token to avoid cascading failures
    // OPTIMIZATION: Make settings fetch non-blocking to prevent router guard timeouts in CI
    if (auth_token_store.current_token) {
      settings_store.startSettingsFetch().catch(err => {
        logger.warn('[ROUTER] Background settings fetch failed, continuing anyway:', err);
      });

      // Hydrate the dashboard search-field catalog from the backend's
      // single source of truth. Dashboard search must not race against
      // the static bootstrap catalog: field types/operators come from
      // GET /document/search/fields.
      const catalogLoad = import('src/services/searchFieldsCatalogService').then(
        ({ loadCatalogFromBackend }) => loadCatalogFromBackend(),
      );
      if (to.path.startsWith('/dashboard')) {
        // Bounded: a stalled /document/search/fields must not block the
        // dashboard from mounting — fall back to the bootstrap catalog.
        await withGuardTimeout(
          catalogLoad.catch((err) => {
            logger.warn('[ROUTER] Search-field catalog hydration failed:', err);
          }),
          GUARD_TIMEOUT_MS.catalog,
          undefined,
          () =>
            logger.warn(
              `[ROUTER] search-field catalog hydration stalled >${GUARD_TIMEOUT_MS.catalog}ms — continuing with bootstrap catalog`,
            ),
        );
      } else {
        void catalogLoad.catch(err => {
          logger.warn('[ROUTER] Search-field catalog hydration failed:', err);
        });
      }
    } else {
      logger.warn('[ROUTER] ⚠️ Skipping settings fetch - no token available yet');
    }

    // Language (incl. backend PREFERRED_LANGUAGE for returning users) is
    // resolved once in src/boot/tolgee.js — not re-applied per navigation here.

    settings_store.checkVersionUpdate();

    // Bounded: ACL hydration must not block navigation — on a stall or failure
    // the app continues with cached/default ACLs (route still renders).
    await withGuardTimeout(
      Acl.get().catch((err) => {
        logger.warn(
          '[ROUTER] ACL hydration failed, continuing with cached/default ACLs:',
          err,
        );
      }),
      GUARD_TIMEOUT_MS.acl,
      undefined,
      () =>
        logger.warn(
          `[ROUTER] ACL hydration stalled >${GUARD_TIMEOUT_MS.acl}ms — continuing with cached/default ACLs`,
        ),
    );

    // ✅ CRITICAL: Mark initialization as complete AFTER all critical initialization steps
    // This prevents logout cascades during app startup when settings/auth APIs fail
    // Only call once per session to avoid resetting the flag
    if (!initializationMarked) {
      initializationMarked = true;
      //markInitializationComplete();
      logger.info(
        '[INIT] 🚀 App initialization complete - logout on API errors now enabled',
      );
    }

    next();
  });

  // On chunk-load failure during navigation, retry once before reloading.
  // Most chunk errors are transient (SW handover, network blip) — retry succeeds
  // silently. Only persistent failures fall through to reload (real deployment).
  Router.onError((error, to, from) => {
    const msg = error.message || '';
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      msg.includes('Loading chunk') ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Couldn\'t resolve component "default"');

    if (!isChunkError) return;

    const retryKey = `chunkRetry:${to.fullPath}`;
    if (!sessionStorage.getItem(retryKey)) {
      console.warn(`[ROUTER] ChunkLoadError on ${from.path} → ${to.path}, retrying`);
      sessionStorage.setItem(retryKey, '1');
      setTimeout(() => sessionStorage.removeItem(retryKey), 30000);
      Router.replace(to.fullPath).catch(() => {});
      return;
    }

    // Retry already failed — reload, with a cooldown to prevent loops
    const lastReload = sessionStorage.getItem('lastChunkReload');
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload) > 10000) {
      sessionStorage.setItem('lastChunkReload', String(now));
      window.location.reload();
    } else {
      notify.warning('Please clear your browser cache and reload the page to access the latest version.');
    }
  });

  return Router;
});
